import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import Raven from 'raven-js';
import debounce from 'transmute/debounce';
import MeetingsStats from 'MeetingsBase/models/MeetingsStats';
import { getHasBusinessUnitsAccess, getHasStarterFeatureAccess as getHasStarterFeatureAccessSelector
// @ts-expect-error untyped import
} from 'MeetingsAdmin/selectors/AccessSelector';
// @ts-expect-error untyped import
import * as IndexActions from '../../redux/actions';
import * as MeetingsLinkActions from '../../redux/actions/MeetingsLinkActions';
// @ts-expect-error untyped import
import * as MeetingsEmailActions from '../../redux/actions/MeetingsEmailActions';
import useWindowWidth from '../../hooks/useWindowWidth';
import { Table } from 'foundations-components/transitional/components/Table';
import { Button } from 'foundations-components/transitional/components/Button';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Text } from 'foundations-components/components/Text';
import MeetingsTableHeader from './MeetingsTableHeader';
import { RESPONSIVE_TABLE_BREAKPOINTS } from '../../constants/numbers';
import { canEditLinkBeingEdited } from '../../selectors/EditMeetingsLinkSelector';
import { ALL_MEETINGS_USERS } from '../../constants/enums';
import MeetingsLinksFilterBar from './MeetingsLinksFilterBar';
import { PAGINATION_COUNT } from './MeetingsLinksTableWrapper';
import MeetingsLinksTableSkeleton from './MeetingsLinksTableSkeleton';
import MeetingsTableRowV2 from './MeetingsTableRowV2';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BREAKPOINT_COL_PERCENTAGES_MAP_WITH_STATS = ImmutableMap({
  [RESPONSIVE_TABLE_BREAKPOINTS.LARGE]: List.of(44, 10, 12, 10, 6, 10, 10),
  [RESPONSIVE_TABLE_BREAKPOINTS.MEDIUM]: List.of(44, 8, 10, 5, 3, 3, 4),
  [RESPONSIVE_TABLE_BREAKPOINTS.SMALL]: List.of(49, 1, 5, 20, 3, 3, 4)
});
const getBreakpoint = width => {
  if (width !== null) {
    if (width >= RESPONSIVE_TABLE_BREAKPOINTS.LARGE) {
      return RESPONSIVE_TABLE_BREAKPOINTS.LARGE;
    }
    if (width >= RESPONSIVE_TABLE_BREAKPOINTS.MEDIUM) {
      return RESPONSIVE_TABLE_BREAKPOINTS.MEDIUM;
    }
  }
  return RESPONSIVE_TABLE_BREAKPOINTS.SMALL;
};
function MeetingsLinksTableV2({
  handleRouteChange,
  canEditAllLinks,
  meetingsLinks,
  refetchLinks,
  isLoading,
  filterValues,
  clearFilters,
  onFilterChange,
  isJitaUser,
  currentUserId,
  pagination,
  userIdParam,
  transferOwnerParam,
  batchUsersData,
  linkStatsData,
  hasError
}) {
  const dispatch = useDispatch();
  const windowWidth = useWindowWidth();
  const hasStarterFeatureAccess = useSelector(getHasStarterFeatureAccessSelector);
  const hasBusinessUnitsAccess = useSelector(getHasBusinessUnitsAccess);

  // @ts-expect-error untyped import
  const meetingsUser = useSelector(state => state.meetingsUser);
  const canEditMoreThanOneLink = useSelector(canEditLinkBeingEdited);
  const [selectedRows, setSelectedRows] = useState(ImmutableSet());
  const [searchInputValueLabel, setSearchInputValueLabel] = useState(filterValues.searchInputValue);
  const debouncedUpdate = useMemo(() => debounce(500, nextSearchInput => onFilterChange({
    search: nextSearchInput
  })), [onFilterChange]);
  useEffect(() => {
    debouncedUpdate(searchInputValueLabel);
  }, [debouncedUpdate, searchInputValueLabel]);
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSearchInputValueLabel('');
  }, [clearFilters]);
  const filtersApplied = useMemo(() => {
    const {
      selectedUserId,
      selectedMeetingType,
      selectedBusinessUnitId,
      searchInputValue
    } = filterValues;
    return selectedUserId !== null || selectedMeetingType !== null || selectedBusinessUnitId !== null || searchInputValue !== '';
  }, [filterValues]);
  const renderedFilterBar = useMemo(() => {
    return /*#__PURE__*/_jsx(MeetingsLinksFilterBar, {
      filterValues: filterValues,
      onFilterChange: onFilterChange,
      clearFilters: handleClearFilters,
      isLoading: isLoading,
      currentUserId: currentUserId,
      hasBusinessUnitsAccess: hasBusinessUnitsAccess,
      breakpoint: getBreakpoint(windowWidth).toString()
    });
  }, [filterValues, onFilterChange, handleClearFilters, isLoading, currentUserId, hasBusinessUnitsAccess, windowWidth]);
  useEffect(() => {
    if (meetingsLinks) {
      const selectedRowsOnThisPage = meetingsLinks.filter(link => selectedRows.has(link.id));
      if (selectedRowsOnThisPage.length !== selectedRows.size) {
        setSelectedRows(ImmutableSet(selectedRowsOnThisPage.map(link => link.id)));
      }
    }
  }, [meetingsLinks, selectedRows]);
  useEffect(() => {
    if (filterValues.selectedUserId === userIdParam && transferOwnerParam && meetingsLinks) {
      const rowIds = meetingsLinks.map(element => element.id);
      const selectedRowOverride = ImmutableSet(rowIds);
      setSelectedRows(selectedRowOverride);
    }
  }, [userIdParam, transferOwnerParam, filterValues.selectedUserId, meetingsLinks]);
  const handleDeleteMeetingsLink = useCallback((linkId, linkOwnerUserId) => {
    dispatch(MeetingsLinkActions.deleteMeetingsLink(linkId, linkOwnerUserId)).then(() => {
      refetchLinks();
    }).catch(error => Raven.captureMessage('Failed to delete meetings link"', {
      extra: {
        error: error.message
      },
      level: 'error'
    }));
  }, [dispatch, refetchLinks]);
  const handleUpdateMeetingsLink = useCallback(link => {
    dispatch(MeetingsLinkActions.updateMeetingsLink(link.convertToJSON())).then(() => {
      refetchLinks();
    }).catch(error => Raven.captureMessage('Failed to update meetings link"', {
      extra: {
        error: error.message
      },
      level: 'error'
    }));
  }, [dispatch, refetchLinks]);
  const handleSendEmbedCode = useCallback((toAddress, linkId) => {
    dispatch(MeetingsEmailActions.sendEmbedCodeEmail(toAddress, linkId));
  }, [dispatch]);
  const shouldOpenTransferModal = useMemo(() => {
    return filterValues.selectedUserId === userIdParam && transferOwnerParam && selectedRows.size === meetingsLinks.length;
  }, [filterValues.selectedUserId, meetingsLinks.length, selectedRows.size, transferOwnerParam, userIdParam]);
  const showPagination = useMemo(() => {
    const {
      selectedUserId,
      selectedMeetingType,
      selectedBusinessUnitId,
      searchInputValue
    } = filterValues;
    return selectedUserId === ALL_MEETINGS_USERS && selectedMeetingType === null && selectedBusinessUnitId === null && searchInputValue === '';
  }, [filterValues]);
  const renderedRows = useMemo(() => {
    return meetingsLinks.map(meetingLink => {
      const linkStatsInfo = linkStatsData && linkStatsData.linkStats.find(stats => meetingLink.id === stats.linkId) || new MeetingsStats();
      const linkOwner = batchUsersData && batchUsersData.batchUsers[meetingLink.userId];
      return /*#__PURE__*/_jsx(MeetingsTableRowV2, {
        link: meetingLink,
        sendEmbedCodeEmail: handleSendEmbedCode,
        deleteMeetingsLink: handleDeleteMeetingsLink,
        updateMeetingsLink: handleUpdateMeetingsLink,
        refetchLinks: refetchLinks,
        clickDefaultLinkTooltip: IndexActions.clickDefaultLinkTooltip,
        handleRouteChange: handleRouteChange,
        clickTableAction: IndexActions.clickTableAction,
        linkStats: linkStatsInfo,
        meetingsUser: meetingsUser,
        onSelectRow: setSelectedRows,
        selectedRows: selectedRows,
        canEditAllLinks: canEditAllLinks,
        selectedUserOption: filterValues.selectedUserId,
        linkOwner: linkOwner,
        canEditMoreThanOneLink: canEditMoreThanOneLink,
        hasStarterFeatureAccess: hasStarterFeatureAccess,
        isJitaUser: isJitaUser,
        shouldRenderBusinessUnit: hasBusinessUnitsAccess
      }, `${meetingLink.link}`);
    });
  }, [batchUsersData, canEditAllLinks, canEditMoreThanOneLink, filterValues.selectedUserId, handleDeleteMeetingsLink, handleRouteChange, handleSendEmbedCode, handleUpdateMeetingsLink, hasBusinessUnitsAccess, hasStarterFeatureAccess, isJitaUser, linkStatsData, meetingsLinks, meetingsUser, refetchLinks, selectedRows]);
  if (hasError) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedFilterBar, /*#__PURE__*/_jsx(ErrorMessage, {
        children: /*#__PURE__*/_jsxs("p", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "table.errorState.header"
          }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "table.errorState.description_jsx",
            options: {
              use: 'link',
              onClick: clearFilters
            },
            elements: {
              Button
            }
          })]
        })
      })]
    });
  }
  if (isLoading) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedFilterBar, /*#__PURE__*/_jsx(MeetingsLinksTableSkeleton, {})]
    });
  }
  if (meetingsLinks.length === 0 && !filtersApplied) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedFilterBar, /*#__PURE__*/_jsx(EmptyState, {
        titleText: /*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "table.emptyState.header"
          })
        }),
        primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "table.emptyState.description"
        }),
        secondaryContent: /*#__PURE__*/_jsx(Illustration, {
          name: "meetings",
          width: "55%"
        })
      })]
    });
  }
  if (meetingsLinks.length === 0 && filtersApplied) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedFilterBar, /*#__PURE__*/_jsx(EmptyState, {
        "data-selenium-test": "filtering-empty-state",
        titleText: /*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "table.noLinksFound.header"
          })
        }),
        primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "table.noLinksFound.description"
        }),
        secondaryContent: /*#__PURE__*/_jsx(Illustration, {
          name: "empty-state-charts",
          width: "55%"
        })
      })]
    });
  }
  let columnWidthsList = BREAKPOINT_COL_PERCENTAGES_MAP_WITH_STATS.get(getBreakpoint(windowWidth).toString());
  if (canEditAllLinks) {
    // Add colspan for admin bulk actions
    columnWidthsList = columnWidthsList.unshift(1);
  }
  const {
    total,
    offset,
    setOffset
  } = pagination;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedFilterBar, /*#__PURE__*/_jsxs(Table, {
      "data-selenium-test": "meetings-links-table",
      children: [/*#__PURE__*/_jsx("colgroup", {
        children: columnWidthsList.map((width, index) => {
          return /*#__PURE__*/_jsx("col", {
            width: `${width}%`
          }, `${width}-${index}`);
        })
      }), /*#__PURE__*/_jsx(MeetingsTableHeader, {
        onSelectRows: setSelectedRows,
        selectedRows: selectedRows,
        meetingsLinks: meetingsLinks,
        canEditAllLinks: canEditAllLinks,
        shouldOpenTransferModal: !!shouldOpenTransferModal,
        shouldRenderBusinessUnit: hasBusinessUnitsAccess,
        onTransferSuccess: handleClearFilters
      }), /*#__PURE__*/_jsx("tbody", {
        "data-test-id": "meetings-table-content",
        children: renderedRows
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "LINK_TABLE_LOAD"
      })]
    }), showPagination && /*#__PURE__*/_jsx(Paginator, {
      page: offset / PAGINATION_COUNT + 1,
      pageCount: total ? Math.ceil(total / PAGINATION_COUNT) : 0,
      onPageChange: e => setOffset((e.target.value - 1) * PAGINATION_COUNT)
    })]
  });
}
export default MeetingsLinksTableV2;